import { Container, Graphics, Sprite, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';

import { GALLOP_FRAMES_PER_M, HORSE_GALLOP } from '../art/sprites/horse';
import {
  CLOUD_BIG,
  CLOUD_SMALL,
  DIRT_TILE,
  GRASS_TILE,
  TREE_BIG,
  TREE_SMALL,
} from '../art/sprites/scenery';
import { bindKeys } from '../input/InputController';
import { DustField, StrideEmitter } from '../render/effects';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { xpFor } from '../sim/minigames/framework';
import { TrailSession, type TerrainKind } from '../sim/minigames/trail';
import { el } from '../ui/dom';
import {
  InputCollector,
  SessionRunner,
  launchPalette,
  showTrainingResults,
  trainingControls,
  type TrainingLaunch,
  type TrainingSceneOptions,
} from './minigameCommon';
import type { Scene, SceneContext } from './SceneManager';

const GROUND_Y = 210;
const HORSE_X = 150;


const TERRAIN_LABEL: Record<TerrainKind, string> = {
  flat: 'FLAT GOING',
  climb: 'CLIMB — she pushes herself',
  descent: 'DESCENT — let her settle',
  creek: 'CREEK — ease off!',
  gust: 'GUSTY — hold the rhythm',
  surge: 'SURGE — open her up!',
};

/** Stamina training: hold ↑ to push, release to breathe, ride the band. */
export class TrailScene implements Scene {
  private readonly launch: TrainingLaunch;
  private readonly options: TrainingSceneOptions;
  private ctx!: SceneContext;
  private session!: TrailSession;
  private runner!: SessionRunner;
  private collector!: InputCollector;
  private container!: Container;
  private world!: Container;
  private meadow!: TilingSprite;
  private dirt!: TilingSprite;
  private horse!: Sprite;
  private gallopFrames!: Texture[];
  private hud!: HTMLElement;
  private needle!: HTMLElement;
  private band!: HTMLElement;
  private hrWrap!: HTMLElement;
  private terrainText!: HTMLElement;
  private timeText!: HTMLElement;
  private perfectText!: HTMLElement;
  private flash!: HTMLElement;
  private unbind: (() => void) | null = null;
  private resultsShown = false;
  private disposeResults: (() => void) | null = null;
  private travelPx = 0;
  private dust!: DustField;
  private readonly emitter = new StrideEmitter();
  private lastDrawMs = 0;
  private perfects = 0;
  private lastNowMs = 0;

  constructor(launch: TrainingLaunch, options: TrainingSceneOptions) {
    this.launch = launch;
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.session = new TrailSession(this.launch.tier, this.launch.seed);
    this.runner = new SessionRunner();
    this.collector = new InputCollector({ holdCodes: ['ArrowUp', 'KeyW'] });

    this.container = new Container();
    ctx.scaler.root.addChild(this.container);
    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 40)
      .fill(0x8fb0d0)
      .rect(0, 40, VIRTUAL_W, 48)
      .fill(0x87a8c8)
      .rect(0, 88, VIRTUAL_W, 10)
      .fill(0x6f94b8);
    this.container.addChild(sky);
    this.meadow = new TilingSprite({
      texture: bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: GROUND_Y - 8 - 98,
    });
    this.meadow.position.set(0, 98);
    this.container.addChild(this.meadow);
    for (const [piece, x, y] of [
      [CLOUD_BIG, 60, 10],
      [CLOUD_SMALL, 320, 24],
    ] as const) {
      const cloud = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
      cloud.position.set(x, y);
      this.container.addChild(cloud);
    }
    this.dirt = new TilingSprite({
      texture: bakeFrames(DIRT_TILE.def, DIRT_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: VIRTUAL_H - (GROUND_Y - 8),
    });
    this.dirt.position.set(0, GROUND_Y - 8);
    this.container.addChild(this.dirt);

    this.world = new Container();
    this.container.addChild(this.world);
    const bigTex = bakeFrames(TREE_BIG.def, TREE_BIG.palette)[0]!;
    const smallTex = bakeFrames(TREE_SMALL.def, TREE_SMALL.palette)[0]!;
    for (let x = 0; x < 4000; x += 170) {
      const big = x % 340 === 0;
      const tree = new Sprite(big ? bigTex : smallTex);
      tree.position.set(x, GROUND_Y - 4 - (big ? TREE_BIG.def.h : TREE_SMALL.def.h));
      this.world.addChild(tree);
    }

    this.dust = new DustField(this.container, 30);
    this.gallopFrames = bakeFrames(HORSE_GALLOP, launchPalette(this.launch));
    this.horse = new Sprite(this.gallopFrames[0]!);
    this.horse.position.set(HORSE_X - HORSE_GALLOP.anchor[0], GROUND_Y - HORSE_GALLOP.h);
    this.container.addChild(this.horse);

    this.needle = el('div', { class: 'hr-needle' });
    this.band = el('div', { class: 'hr-band' });
    this.hrWrap = el('div', { class: 'hr-wrap' }, this.band, this.needle);
    this.terrainText = el('div', { class: 'race-banner' });
    this.timeText = el('span', { class: 'hud-chip' });
    this.perfectText = el('span', { class: 'hud-chip' });
    this.flash = el('div', { class: 'race-flash' });
    this.hud = el(
      'div',
      { class: 'drill-hud with-training-controls' },
      el('div', { class: 'ranch-topbar' }, this.timeText, this.perfectText),
      this.terrainText,
      this.flash,
      el(
        'div',
        { class: 'hr-dock' },
        el('div', { class: 'stamina-label' }, 'HEART RATE'),
        this.hrWrap,
      ),
      trainingControls(this.collector, [
        { action: 'primary', mode: 'hold', label: 'HOLD ↑', ariaLabel: 'Hold to push' },
      ]),
      el('div', { class: 'ranch-hints' }, 'Hold ↑ / W or HOLD to push · release to breathe · stay in the green'),
    );
    ctx.uiRoot.append(this.hud);

    this.unbind = bindKeys([{ code: 'Escape', handler: () => this.endEarly() }]);
  }

  frame(nowMs: number): void {
    this.lastNowMs = nowMs;
    const steps = this.runner.advance(nowMs, this.session, this.collector);
    for (const event of this.session.drainEvents()) {
      switch (event.kind) {
        case 'segment':
          this.terrainText.textContent = TERRAIN_LABEL[event.terrain];
          break;
        case 'perfect':
          this.perfects += 1;
          this.showFlash('PERFECT STRETCH!', 'good');
          break;
        case 'blown':
          this.showFlash('BLOWN UP!', 'bad');
          break;
        default:
          break;
      }
    }
    this.draw(steps);
    if (this.session.finished && !this.resultsShown) this.showResults();
  }

  private draw(steps: number): void {
    const session = this.session;
    // Scroll speed tracks the needle — pushing feels like pushing.
    this.travelPx += steps * (1 / 30) * (5 + session.needle * 9) * 16;
    this.world.position.x = -Math.round(this.travelPx % 4000);
    // Ground scrolls with the trees; the meadow drifts behind as parallax.
    this.dirt.tilePosition.x = -Math.round(this.travelPx);
    this.meadow.tilePosition.x = -Math.round(this.travelPx * 0.4);
    this.horse.texture =
      this.gallopFrames[Math.floor((this.travelPx / 16) * GALLOP_FRAMES_PER_M) % this.gallopFrames.length]!;

    const center = session.bandCenterNow();
    const half = session.config.bandHalf;
    this.band.style.left = `${((center - half) * 100).toFixed(1)}%`;
    this.band.style.width = `${(half * 2 * 100).toFixed(1)}%`;
    this.needle.style.left = `${(session.needle * 100).toFixed(1)}%`;
    const inBand = Math.abs(session.needle - center) <= half;
    this.hrWrap.classList.toggle('in-band', inBand);
    this.hrWrap.classList.toggle('red', session.needle > 0.9);
    this.hrWrap.classList.toggle('rhythm', session.rhythmActive);

    const nowMs = performance.now();
    const dtS = this.lastDrawMs === 0 ? 0 : Math.min(0.05, (nowMs - this.lastDrawMs) / 1000);
    this.lastDrawMs = nowMs;
    this.dust.update(dtS);
    if (this.emitter.step(this.travelPx / 16, 2.4)) {
      this.dust.emit(HORSE_X - 14, GROUND_Y, 0.5 + session.needle);
    }

    const timeLeft = Math.max(0, (session.durationTicks - session.tick) / 30);
    this.timeText.textContent = `⏱ ${timeLeft.toFixed(1)}s`;
    this.perfectText.textContent = `perfect ×${this.perfects}`;
  }

  private endEarly(): void {
    if (this.session.finished) return;
    this.runner.fastForward(this.session);
  }

  private showFlash(text: string, tone: 'good' | 'bad'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash';
    void this.flash.offsetWidth;
    this.flash.className = `race-flash show ${tone}`;
  }

  private showResults(): void {
    this.resultsShown = true;
    const score = this.session.score();
    const xp = xpFor(score.raw, this.launch.xpMult);
    const finish = (): void => {
      const tape = [...this.runner.tape];
      this.ctx.scenes.pop();
      this.options.onDone(tape);
    };
    this.disposeResults = showTrainingResults(
      this.ctx.uiRoot,
      { title: 'Trail ride done', detail: score.detail, xp, failed: score.failed },
      finish,
    );
    this.unbind?.();
    this.unbind = bindKeys([
      { code: 'Enter', inModal: true, handler: finish },
      { code: 'Escape', inModal: true, handler: finish },
    ]);
  }

  exit(): void {
    this.unbind?.();
    this.collector.dispose();
    this.disposeResults?.();
    this.hud.remove();
    this.container.destroy({ children: true });
  }
}
